import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface NotificationContextType {
  registerFlashHandler: (key: string, handler: () => void) => void;
  unregisterFlashHandler: (key: string) => void;
}

// The set of URL query param keys this system owns. Only these are captured
// and stripped — unrelated params like feature flags are never touched.
const FLASH_KEYS = new Set(['designSubmitted']);

// {} as NotificationContextType matches the codebase pattern (see useDialog,
// useAppState). The hook guards against use outside a provider below.
const NotificationContext = createContext({} as NotificationContextType);

export function NotificationProvider({ children }: PropsWithChildren) {
  // Immutable snapshot of flash keys present in the URL at render time.
  // Used exclusively by the URL-strip effect — never mutated, so stripping
  // is not affected by runtime consumption via pendingRef.
  const capturedKeysRef = useRef(
    new Set(
      [...new URLSearchParams(window.location.search).entries()]
        .filter(([key, value]) => FLASH_KEYS.has(key) && value === 'true')
        .map(([key]) => key),
    ),
  );

  // Mutable set of keys still waiting for a handler to claim them.
  // Populated from capturedKeysRef; keys are deleted as handlers fire.
  // Initialised synchronously during render so child useEffect calls to
  // registerFlashHandler always see the correct pending state — child effects
  // run before parent effects in React.
  const pendingRef = useRef(new Set(capturedKeysRef.current));

  // Strip only the captured flash keys from the URL on mount, preserving any
  // other query params and the hash fragment.
  useEffect(() => {
    if (capturedKeysRef.current.size === 0) return;

    const params = new URLSearchParams(window.location.search);
    capturedKeysRef.current.forEach(key => params.delete(key));

    const remaining = params.toString();
    const url =
      window.location.pathname +
      (remaining ? `?${remaining}` : '') +
      window.location.hash;

    window.history.replaceState(null, '', url);
  }, []);

  // When a handler registers, fire immediately if its key was captured from
  // the URL. Handlers are one-shot: once fired the key is removed from pending.
  const registerFlashHandler = useCallback(
    (key: string, handler: () => void): void => {
      if (pendingRef.current.has(key)) {
        pendingRef.current.delete(key);
        handler();
      }
    },
    [],
  );

  const unregisterFlashHandler = useCallback((key: string): void => {
    // No-op: handlers are one-shot and fired at registration time.
    // Exists so consumers can call it symmetrically in useEffect cleanup.
    void key;
  }, []);

  const memoedValue = useMemo(
    () => ({ registerFlashHandler, unregisterFlashHandler }),
    [registerFlashHandler, unregisterFlashHandler],
  );

  return (
    <NotificationContext.Provider value={memoedValue}>
      {children}
    </NotificationContext.Provider>
  );
}

export default function useNotification(): NotificationContextType {
  const context = useContext(NotificationContext);

  if (!context.registerFlashHandler) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }

  return context;
}
